import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { EmbedDialogSettings } from '@common/entities/dialog/embed-dialog.entity';
import { ExportDialogOutput, ExportDialogOutputSuccess } from '@common/entities/dialog/export-dialog.entity';
import { EmbedDialogComponent } from '@common/modules/dialog/embed-dialog/embed-dialog.component';
import { ExportDialogComponent } from '@common/modules/dialog/export-dialog/export-dialog.component';
import { RecordErrorDialogComponent } from '@common/modules/dialog/record-error-dialog/record-error-dialog.component';
import { RecordsSandbox } from '@common/modules/records/sandbox/records.sandbox.service';
import { combineLatest, merge, Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';

import { ActionService } from '@common/sandboxes/actions/action-services/abstract/action.service.interface';
import { AddObjectsActionService } from '@common/sandboxes/actions/action-services/intellectual-actions/add-objects/add-objects.action.service';
import { SetCoverActionService } from '@common/sandboxes/actions/action-services/intellectual-actions/set-cover/set-cover.action.service';
import { CreateRelationActionService } from '@common/sandboxes/actions/action-services/record-actions/create-relation/create-relation.action.service';
import { DeleteActionService } from '@common/sandboxes/actions/action-services/record-actions/delete/delete.action.service';
import { PermanentDeleteActionService } from '@common/sandboxes/actions/action-services/record-actions/permanent-delete/permanent-delete.action.service';
import { PublishActionService } from '@common/sandboxes/actions/action-services/record-actions/publish/publish.action.service';
import { RecoverRecordActionService } from '@common/sandboxes/actions/action-services/record-actions/recover-record/recover-record.action.service';
import { SubmitActionService } from '@common/sandboxes/actions/action-services/record-actions/submit/submit.action.service';
import { AddToSelectionActionService } from '@common/sandboxes/actions/action-services/selection-actions/add-to-selection/add-to-selection.action.service';
import { RemoveFromSelectionActionService } from '@common/sandboxes/actions/action-services/selection-actions/remove-from-selection/remove-from-selection.action.service';
import {
    ActionItemFunction,
    ActionItemOptions,
    ActionsService,
    ActionTooltipHelper,
    asyncIconActionItemStreamHelper,
    DownloadsService,
    ExportsService,
    GenericActionsSandbox,
    GetExportPreviewUserAction,
    GetShareUserAction,
    MediahavenShareDialogComponent,
    MediahavenShareDialogConfig,
    MoreActionsButton,
    PersonalSelectionService,
    SearchService,
    UserAction,
    UserExportLocationsService
} from '@mediahaven/shared';
import {
    ActionGroup,
    ActionGroupStyle,
    ActionItem,
    ActionItemStyle,
    ApiActionBundle,
    ApiFragment,
    ApplicationConfigService,
    BatchActions,
    BatchObject,
    BatchService,
    BulkSearchRequest,
    CsvDialogResult,
    DialogConfig,
    DialogRef,
    DialogStyle,
    ExportRequest,
    IExportJob,
    NotificationType,
    ProcessingProgressDialogContainerComponent,
    ProfileDataService,
    ShCsvExportDialog,
    ShDynamicDialogService,
    Toast,
    UrlBuilderService
} from '@stylehaven/components';
import { CoupleToParentActionService } from '../action-services/intellectual-actions/couple-to-parent/couple-to-parent.action.service';
import { DecoupleFromParentActionService } from '../action-services/intellectual-actions/decouple-from-parent/decouple-from-parent.action.service';
import { OpenContentActionService } from '../action-services/intellectual-actions/open-content/open-content.action.service';
import { AddNewOriginalActionService } from '../action-services/record-actions/add-new-original/add-new-original.action.service';
import { ContactAdminActionService } from '../action-services/record-actions/contact-admin/contact-admin.action.service';
import { OpenDetailActionService } from '../action-services/record-actions/open-detail/open-detail.action.service';
import { OpenEditActionService } from '../action-services/record-actions/open-edit/open-edit.action.service';
import { OpenInMediaPlayerActionService } from '../action-services/record-actions/open-in-media-player/open-in-media-player.action.service';
import { OpenInViewerActionService } from '../action-services/record-actions/open-in-viewer/open-in-viewer.action.service';
import { OpenSimilaritySearchActionService } from '../action-services/record-actions/open-similarity-search/open-similarity-search.action.service';
import { EmptySelectionActionService } from '../action-services/selection-actions/empty-selection/empty-selection.action.service';
import { MediaHavenActionType, MH_ICON_ACTIONS } from '../entities/action-types.entity';
import { GetCsvExportUserAction } from '../helpers/shared-actions/csv-export.action';
import { GetEmbedUserAction } from '../helpers/shared-actions/embed.action';
import { GetExportUserAction } from '../helpers/shared-actions/export.action';
import { GetShowErrorsUserAction } from '../helpers/shared-actions/show-errors.action';
import { GetShowProcessingProgressUserAction } from '../helpers/shared-actions/show-progress.action';

@Injectable({ providedIn: 'root' })
export class ActionsSandbox extends GenericActionsSandbox {
    private readonly profileService: ProfileDataService = inject(ProfileDataService);

    // Regular actions
    private readonly addObjectsActionService: AddObjectsActionService = inject(AddObjectsActionService);
    private readonly addNewOriginalActionService: AddNewOriginalActionService = inject(AddNewOriginalActionService);
    private readonly addToSelectionActionService: AddToSelectionActionService = inject(AddToSelectionActionService);
    private readonly coupleToParentActionService: CoupleToParentActionService = inject(CoupleToParentActionService);
    private readonly decoupleFromParentActionService: DecoupleFromParentActionService = inject(
        DecoupleFromParentActionService
    );

    private readonly emptySelectionActionService: EmptySelectionActionService = inject(EmptySelectionActionService);
    private readonly removeFromSelectionActionService: RemoveFromSelectionActionService = inject(
        RemoveFromSelectionActionService
    );

    private readonly createRelationActionService: CreateRelationActionService = inject(CreateRelationActionService);
    private readonly deleteActionService: DeleteActionService = inject(DeleteActionService);
    private readonly publishActionService: PublishActionService = inject(PublishActionService);
    private readonly submitActionService: SubmitActionService = inject(SubmitActionService);
    private readonly permanentDeleteActionService: PermanentDeleteActionService = inject(PermanentDeleteActionService);
    private readonly recoverRecordActionService: RecoverRecordActionService = inject(RecoverRecordActionService);
    private readonly setCoverActionService: SetCoverActionService = inject(SetCoverActionService);

    // Don't add link actions here, they are handled separately
    private readonly actionServices: ActionService[] = [
        this.addObjectsActionService,
        this.addNewOriginalActionService,
        this.addToSelectionActionService,
        this.coupleToParentActionService,
        this.decoupleFromParentActionService,
        this.emptySelectionActionService,
        this.removeFromSelectionActionService,
        this.removeFromSelectionActionService,
        this.createRelationActionService,
        this.deleteActionService,
        this.publishActionService,
        this.submitActionService,
        this.permanentDeleteActionService,
        this.recoverRecordActionService,
        this.setCoverActionService
    ];

    // Link actions
    private readonly contactAdminActionService: ContactAdminActionService = inject(ContactAdminActionService);
    private readonly openContentActionService: OpenContentActionService = inject(OpenContentActionService);
    private readonly openDetailActionService: OpenDetailActionService = inject(OpenDetailActionService);
    private readonly openEditActionService: OpenEditActionService = inject(OpenEditActionService);
    private readonly openInMediaPlayerActionService: OpenInMediaPlayerActionService =
        inject(OpenInMediaPlayerActionService);

    private readonly openInViewerActionService: OpenInViewerActionService = inject(OpenInViewerActionService);
    private readonly openSimilaritySearch: OpenSimilaritySearchActionService = inject(
        OpenSimilaritySearchActionService
    );

    constructor(
        protected actionsService: ActionsService,
        protected dialogService: ShDynamicDialogService,
        protected tooltipHelper: ActionTooltipHelper,
        protected searchService: SearchService,
        private readonly recordSandbox: RecordsSandbox,
        private readonly personalSelectionService: PersonalSelectionService,
        private readonly router: Router,
        private readonly configService: ApplicationConfigService,
        private readonly exportLocationService: UserExportLocationsService,
        private readonly downloadsService: DownloadsService,
        private readonly urlBuilderService: UrlBuilderService,
        private readonly exportsService: ExportsService,
        private readonly batchService: BatchService
    ) {
        super(actionsService, dialogService, tooltipHelper, searchService);

        this.initPerformedActionsListeners();
    }

    private readonly mapping: Record<MediaHavenActionType, ActionItemFunction<MediaHavenActionType>> = {
        [MediaHavenActionType.ADD_OBJECTS]: this.addObjectsActionService.getActionItem,
        [MediaHavenActionType.ADD_TO_SELECTION]: this.addToSelectionActionService.getActionItem,
        [MediaHavenActionType.ADD_NEW_ORIGINAL]: this.addNewOriginalActionService.getActionItem,
        [MediaHavenActionType.CONTACT_ADMIN]: this.contactAdminActionService.getActionItem,
        [MediaHavenActionType.COUPLE_TO_PARENT]: this.coupleToParentActionService.getActionItem,
        [MediaHavenActionType.CREATE_RELATION]: this.createRelationActionService.getActionItem,
        [MediaHavenActionType.CSV_EXPORT]: this.getCsvExportActionItem,
        [MediaHavenActionType.DECOUPLE_FROM_PARENT]: this.decoupleFromParentActionService.getActionItem,
        [MediaHavenActionType.DELETE]: this.deleteActionService.getActionItem,
        [MediaHavenActionType.DOWNLOAD]: this.getDownloadActionItem,
        [MediaHavenActionType.DOWNLOAD_PREVIEW]: this.getDownloadPreviewActionItem,
        [MediaHavenActionType.EDIT]: this.openEditActionService.getActionItem,
        [MediaHavenActionType.EMBED]: this.getCreateEmbeddedCodeActionItem,
        [MediaHavenActionType.EMPTY_SELECTION]: this.emptySelectionActionService.getActionItem,
        [MediaHavenActionType.OPEN_CONTENT]: this.openContentActionService.getActionItem,
        [MediaHavenActionType.OPEN_IN_EXTENDED_MEDIA_PLAYER]: this.openInMediaPlayerActionService.getActionItem,
        [MediaHavenActionType.PERMANENT_DELETE]: this.permanentDeleteActionService.getActionItem,
        [MediaHavenActionType.PUBLISH]: this.publishActionService.getActionItem,
        [MediaHavenActionType.RECOVER_RECORD]: this.recoverRecordActionService.getActionItem,
        [MediaHavenActionType.REMOVE_FROM_SELECTION]: this.removeFromSelectionActionService.getActionItem,
        [MediaHavenActionType.SET_AS_COLLECTION_COVER]: this.setCoverActionService.getActionItem,
        [MediaHavenActionType.SHARE]: this.getShareActionItem,
        [MediaHavenActionType.SHARE_SELECTION]: this.getShareSelectionActionItem,
        [MediaHavenActionType.SHOW_ERRORS]: this.getShowErrorsActionItem,
        [MediaHavenActionType.SHOW_PROCESSING_PROGRESS]: this.getShowProcessingProgressActionItem,
        [MediaHavenActionType.SUBMIT_FOR_EVALUATION]: this.submitActionService.getActionItem,
        [MediaHavenActionType.WATCH]: this.openDetailActionService.getActionItem,
        [MediaHavenActionType.OPEN_IN_VIEWER]: this.openInViewerActionService.getActionItem,
        [MediaHavenActionType.SIMILARITY_SEARCH]: this.openSimilaritySearch.getActionItem
    };

    public getActionsJoined(
        actionTypes: readonly MediaHavenActionType[],
        record: ApiFragment,
        actionItemOptions: ActionItemOptions = {}
    ): Observable<ActionItem[]> {
        const baseActionItems: Observable<ActionItem | null>[] = this.getActions(
            actionTypes,
            record,
            actionItemOptions
        );
        const apiActionItems$: Observable<(ActionItem | null)[]> = this.getApiActions(record);

        return this.combineActionStreams(baseActionItems, apiActionItems$);
    }

    public getActions(
        actionTypes: readonly MediaHavenActionType[],
        record: ApiFragment,
        actionItemOptions: ActionItemOptions = {}
    ): Observable<ActionItem | null>[] {
        return actionTypes.map(actionType => this.getActionItem(actionType, record, { ...actionItemOptions }));
    }

    public getActionsAsSingle(
        actionTypes: readonly MediaHavenActionType[],
        record: ApiFragment,
        actionItemOptions: ActionItemOptions = {}
    ): Observable<ActionGroup> {
        const actionItems$: Observable<ActionItem[]> = this.getActionsJoined(actionTypes, record, actionItemOptions);
        return actionItems$.pipe(
            map(actionItems => (actionItems.length ? [MoreActionsButton(actionItems)] : [])),
            map(actions => ({ actions }))
        );
    }

    public getActionItem(
        actionType: MediaHavenActionType,
        record: ApiFragment,
        actionItemOptions: ActionItemOptions = {}
    ): Observable<ActionItem | null> {
        try {
            return this.mapping[actionType].call(this, record, actionType, actionItemOptions);
        } catch (error) {
            console.error(`Something went wrong during the loading of ${actionType}: ${error}`);
            return of(null);
        }
    }

    public getRegularActionsWithIconActions(
        actionTypes: readonly string[],
        record: ApiFragment,
        actionItemOptions: ActionItemOptions = {}
    ): Observable<ActionGroup> {
        const iconActionTypes: string[] = actionTypes.filter(type =>
            MH_ICON_ACTIONS.includes(type as MediaHavenActionType)
        );
        const nonIconActionTypes: readonly string[] = actionTypes;

        const iconActions: Observable<ActionItem | null>[] = this.getActions(
            iconActionTypes as MediaHavenActionType[],
            record,
            { ...actionItemOptions, withIcon: true }
        );

        const nonIconActions: Observable<ActionItem | null>[] = this.getActions(
            nonIconActionTypes as MediaHavenActionType[],
            record,
            actionItemOptions
        );
        const apiActions$: Observable<ApiActionBundle> = this.getApiActionsAsBundle(record);

        return asyncIconActionItemStreamHelper(iconActions, nonIconActions, apiActions$).pipe(
            map(actions => ({ actions, style: ActionGroupStyle.Collapsed }))
        );
    }

    private initPerformedActionsListeners(): void {
        // Add all ActionServices performedAction subjects here
        merge(...this.actionServices.map(service => service.performedAction$))
            .pipe(takeUntil(this.destroy$))
            .subscribe(action =>
                this.emitActionPerformed(action.action, action.target, action.successful, action.warrantsRefresh)
            );
    }

    // #region Action Items
    private getCsvExportActionItem(
        record: ApiFragment,
        action: MediaHavenActionType,
        actionItemOptions: ActionItemOptions = {}
    ): Observable<ActionItem | null> {
        return this.profileService.getNumberOfExportProfiles().pipe(
            map(exportProfileCount => {
                const userAction: UserAction = GetCsvExportUserAction(record, exportProfileCount);

                if (!userAction.isAvailable()) {
                    return null;
                }

                return {
                    label: this.translate('actions.csv-export'),
                    eventListener: () => this.onCsvExport(record, action),
                    disabled: userAction.isDisabled(),
                    disabledInfoMsg: this.tooltipHelper.getActionTooltip(userAction, [record])
                };
            })
        );
    }

    private getDownloadPreviewActionItem(
        record: ApiFragment,
        action: MediaHavenActionType,
        actionItemOptions: ActionItemOptions = {}
    ): Observable<ActionItem | null> {
        const userAction: UserAction = GetExportPreviewUserAction(record);

        if (!userAction.isAvailable()) {
            return of(null);
        }

        const icon: string = !record.isConcept() && actionItemOptions.withIcon ? 'download' : '';
        const style: ActionItemStyle = icon === '' ? ActionItemStyle.Primary : ActionItemStyle.PureIconButton;

        return of({
            label: record.isNewCollection()
                ? this.translate('actions.download.download-previews')
                : this.translate('actions.download.download-preview'),
            eventListener: this.onDownloadPreview.bind(this, record, action),
            disabled: userAction.isDisabled(),
            disabledInfoMsg: this.tooltipHelper.getActionTooltip(userAction, [record]),
            icon,
            style
        });
    }

    private getDownloadActionItem(
        record: ApiFragment,
        action: MediaHavenActionType,
        actionItemOptions: ActionItemOptions = {}
    ): Observable<ActionItem | null> {
        const userAction$: Observable<UserAction> = this.exportLocationService.downloadableLocations$.pipe(
            map(downloadableLocations => GetExportUserAction(record, downloadableLocations))
        );

        const icon: string = !record.isConcept() && actionItemOptions.withIcon ? 'cloud-download-alt' : '';
        const style: ActionItemStyle = icon === '' ? ActionItemStyle.Primary : ActionItemStyle.PureIconButton;

        return userAction$.pipe(
            map(userAction => {
                if (!userAction.isAvailable()) {
                    return null;
                }

                return {
                    label: this.translate('actions.download.main'),
                    eventListener: this.onExport.bind(this, record, action),
                    disabled: userAction.isDisabled(),
                    disabledInfoMsg: this.tooltipHelper.getActionTooltip(userAction, [record]),
                    icon,
                    style
                };
            })
        );
    }

    private getShareActionItem(
        record: ApiFragment,
        action: MediaHavenActionType,
        actionItemOptions: ActionItemOptions = {}
    ): Observable<ActionItem | null> {
        const userAction$: Observable<UserAction> = this.getUserFunctions().pipe(
            map(userFunctions => GetShareUserAction(record, userFunctions, !!actionItemOptions.shareContext))
        );

        const icon: string = !record.isConcept() && actionItemOptions.withIcon ? 'share-alt-square' : '';
        const style: ActionItemStyle = icon === '' ? ActionItemStyle.Primary : ActionItemStyle.PureIconButton;

        return userAction$.pipe(
            map(userAction => {
                if (!userAction.isAvailable()) {
                    return null;
                }

                return {
                    label: this.translate('actions.share'),
                    eventListener: this.onShare.bind(this, record, action),
                    disabled: userAction.isDisabled(),
                    disabledInfoMsg: this.tooltipHelper.getActionTooltip(userAction, [record]),
                    icon,
                    style
                };
            })
        );
    }

    private getShareSelectionActionItem(
        record: ApiFragment,
        action: MediaHavenActionType,
        actionItemOptions: ActionItemOptions = {}
    ): Observable<ActionItem | null> {
        if (actionItemOptions.shareContext) {
            return of(null);
        }

        const isSelection: boolean = record.isSelection();
        const selectionHasNoChildren: boolean = record.getRelationFragmentIdsOfType('Contains').length === 0;
        const isSelectionWithoutChildren: boolean = isSelection && selectionHasNoChildren;

        const userAction: UserAction = new UserAction(
            [],
            [{ condition: isSelectionWithoutChildren, tooltip: 'selection.empty' }]
        );

        return of({
            label: this.translate('actions.share'),
            eventListener: this.onShareSelection.bind(this, record, action),
            disabled: userAction.isDisabled(),
            disabledInfoMsg: this.tooltipHelper.getActionTooltip(userAction, [record])
        });
    }

    private getCreateEmbeddedCodeActionItem(
        record: ApiFragment,
        action: MediaHavenActionType
    ): Observable<ActionItem | null> {
        const userAction: UserAction = GetEmbedUserAction(record);

        if (!userAction.isAvailable()) {
            return of(null);
        }

        return of({
            label: this.translate('actions.embed'),
            eventListener: this.onCreateEmbeddedCode.bind(this, record, action),
            disabled: userAction.isDisabled(),
            disabledInfoMsg: this.tooltipHelper.getActionTooltip(userAction, [record])
        });
    }

    private getShowErrorsActionItem(
        record: ApiFragment,
        action: MediaHavenActionType,
        actionItemOptions: ActionItemOptions = {}
    ): Observable<ActionItem | null> {
        const userAction: UserAction = GetShowErrorsUserAction(record);

        if (!userAction.isAvailable()) {
            return of(null);
        }

        return of({
            label: this.translate('actions.show-errors'),
            eventListener: () => this.onShowErrors(record),
            disabled: userAction.isDisabled(),
            disabledInfoMsg: this.tooltipHelper.getActionTooltip(userAction, [record])
        });
    }

    private getShowProcessingProgressActionItem(
        record: ApiFragment,
        action: MediaHavenActionType,
        actionItemOptions: ActionItemOptions = {}
    ): Observable<ActionItem | null> {
        const userAction: UserAction = GetShowProcessingProgressUserAction(record);

        if (!userAction.isAvailable()) {
            return of(null);
        }

        return of({
            label: this.translate('actions.show-progress'),
            eventListener: () => this.onShowProcessingProgress(record),
            disabled: userAction.isDisabled(),
            disabledInfoMsg: this.tooltipHelper.getActionTooltip(userAction, [record])
        });
    }
    // #endregion

    // Implementation

    // #region Download
    private async onDownloadPreview(record: ApiFragment, action: MediaHavenActionType): Promise<void> {
        return this.downloadsService.downloadFragmentPreview(record).catch(error => this.downloadPreviewError(error));
    }

    public downloadPreviewError(error: Error): void {
        console.error(error);
        this.createAndAddNewToast('action-toasts.download-preview.error', NotificationType.Error);
    }

    private async onExport(record: ApiFragment, action: MediaHavenActionType): Promise<void> {
        const title: string = this.translate('actions.download.main');

        const ok: string = this.translate('actions.download.prepare');
        const cancel: string = this.translate('actions.common.cancel');
        const data: any = { records: [record], ok, cancel };

        this.openExportDialog(record, action, {
            title,
            data
        });
    }

    private openExportDialog(record: ApiFragment, action: MediaHavenActionType, config: DialogConfig<any>): void {
        const actionBusyToast: Toast = this.createActionBusyToast(
            'action-toasts.download-fragment-request',
            record.getMh2Entity()
        );

        this.initExportDialog(record, config, actionBusyToast)
            .pipe(
                tap(() => this.closeToast(actionBusyToast.id)),
                tap((output: any) => this.downloadFragmentRequestSuccess(record, output)),
                tap((output: any) =>
                    output.DownloadUrl ? this.downloadsService.downloadUrl(output.DownloadUrl) : null
                ),
                tap(() => this.emitActionPerformed(action, record, true, false)),
                catchError(error => {
                    this.closeToast(actionBusyToast.id);
                    this.emitActionPerformed(action, record, false, false);
                    this.downloadFragmentRequestFailure(error, record);
                    return of(error);
                }),
                take(1)
            )
            .subscribe();
    }

    private initExportDialog(
        record: ApiFragment,
        config: DialogConfig<any>,
        toast: Toast
    ): Observable<ExportDialogOutputSuccess | IExportJob> {
        const dialogConfig: DialogConfig = {
            options: {
                style: DialogStyle.Wide
            },
            ...config
        };
        const ref: DialogRef<ExportDialogOutput> = this.dialogService.open(ExportDialogComponent, dialogConfig);

        let exportRecords$: Observable<ApiFragment[]>;
        if (record.isSelection()) {
            exportRecords$ = this.personalSelectionService.getAllExportableSelectionFragments(record.getFragmentId());
        } else {
            exportRecords$ = of([record]);
        }

        const dialog$: Observable<ExportDialogOutputSuccess> = ref.afterClosed$.pipe(
            filter(val => !!val),
            filter((val: ExportDialogOutput): val is ExportDialogOutputSuccess => val.hasOwnProperty('exportDirect')),
            tap(() => this.addToast(toast))
        );

        return combineLatest([dialog$, exportRecords$]).pipe(
            switchMap(([output, records]) =>
                this.exportsService
                    .exportRecords(this.addRecordsToExport(output.exportRequest, records))
                    .pipe(
                        switchMap(([results]: IExportJob[]) =>
                            output.exportDirect
                                ? this.recordSandbox.getDownloadStatus(results.ExportJobId)
                                : of(results)
                        )
                    )
            )
        );
    }

    private addRecordsToExport(request: ExportRequest, records: ApiFragment[]): ExportRequest {
        request.Records = records.map(record => ({ RecordId: record.getFragmentId() }));
        return request;
    }

    private downloadFragmentRequestSuccess(record: ApiFragment, output: any): void {
        const translateKey: string = output.DownloadUrl
            ? 'action-toasts.download-direct'
            : 'action-toasts.download-fragment-request';
        this.createAndAddActionSuccessToast(
            translateKey,
            record.getMh2Entity(),
            1,
            [],
            'navigation.see-details',
            '/downloads'
        );
    }

    private downloadFragmentRequestFailure(error: HttpErrorResponse, record: ApiFragment): void {
        console.error('Exporting fragment failed', error);
        this.createAndAddActionErrorToast('action-toasts.download-fragment-request', record.getMh2Entity(), 1);
    }

    // #endregion

    // #region Share
    private onShare(record: ApiFragment, action: MediaHavenActionType): void {
        const title: string = this.translate('actions.share');
        const cancel: string = this.translate('actions.common.cancel');

        const config: DialogConfig<MediahavenShareDialogConfig> = {
            title,
            data: {
                cancel,
                records: [record]
            },
            options: {
                style: DialogStyle.Wide
            }
        };

        const ref: DialogRef<boolean> = this.dialogService.open(MediahavenShareDialogComponent, config);

        ref.afterClosed$.pipe(take(1)).subscribe(copySuccessful => {
            if (copySuccessful) {
                this.emitActionPerformed(action, record, true, false);
            } else {
                this.emitActionPerformed(action, record, false, false);
            }
        });
    }

    // #endregion

    // #region CSV Export
    private onCsvExport(record: ApiFragment, action: MediaHavenActionType): void {
        const title: string = this.translate('actions.csv-export');
        const ok: string = this.translate('actions.csv-export');
        const cancel: string = this.translate('actions.common.cancel');

        const config: DialogConfig = {
            title,
            data: {
                exportProfiles$: this.profileService.getAllExportProfiles(),
                cancel,
                ok
            },
            options: {
                style: DialogStyle.Wide
            }
        };

        this.dialogService
            .open<CsvDialogResult>(ShCsvExportDialog, config)
            .afterClosed$.pipe(
                switchMap(config =>
                    config.type === 'submit' ? this.startCsvExport(record, action, config.profileId) : of(undefined)
                ),
                take(1)
            )
            .subscribe();
    }

    private startCsvExport(
        record: ApiFragment,
        action: MediaHavenActionType,
        profileId: string
    ): Observable<BatchObject> {
        const exportFileName: string = `${record.getTitle()}.csv`;
        const searchRequest: BulkSearchRequest = new BulkSearchRequest(
            [record],
            this.recordSandbox.getAllRecordsZone()
        );

        const actionBusyToast: Toast = this.createActionBusyToast('action-toasts.csv-export', 'all');

        this.addToast(actionBusyToast);

        return this.batchService
            .sendBatch(BatchActions.CSV_EXPORT, {
                fragments: [record],
                searchRequest: searchRequest,
                waitForFinish: true,
                isBulk: false,
                profileId: profileId,
                exportFileName
            })
            .pipe(
                tap(() => this.closeToast(actionBusyToast.id)),
                tap(() =>
                    this.createAndAddNewToast(
                        'action-toasts.csv-export.success.all',
                        NotificationType.Success,
                        'navigation.see-details',
                        '/downloads'
                    )
                ),
                tap(() => this.emitActionPerformed(action, record, true)),
                catchError(error => {
                    this.closeToast(actionBusyToast.id);
                    this.emitActionPerformed(action, record, false, false);
                    this.createAndAddActionErrorToast('action-toasts.csv-export', 'all');
                    return of(error);
                })
            );
    }
    // #endregion

    // #region Share Selection
    private onShareSelection(selection: ApiFragment, action: MediaHavenActionType): void {
        this.onShare(selection, action);
    }
    // #endregion

    //#region Embed
    private onCreateEmbeddedCode(record: ApiFragment, action: MediaHavenActionType): void {
        const title: string = this.translate('actions.embed');
        const embedUrl: string | undefined = this.configService.getInstant('embedUrl');

        if (!embedUrl) {
            console.error('EmbedUrl is not configured');
            return;
        }

        const dialogConfig: EmbedDialogSettings = {
            record,
            embedUrl,
            copyToClipboardLabel: this.translate('media.embed-dialog.copy-to-clipboard'),
            cancelLabel: this.translate('actions.common.cancel'),
            widthLabel: this.translate('general.width'),
            heightLabel: this.translate('general.height'),
            autoplayLabel: this.translate('media.embed-dialog.autoplay'),
            subtitle: this.translate('media.embed-dialog.subtitle')
        };

        const ref: DialogRef<boolean> = this.dialogService.open(EmbedDialogComponent, {
            title,
            data: dialogConfig,
            options: {
                style: DialogStyle.Wide
            }
        });

        ref.afterClosed$.pipe(take(1)).subscribe(copySuccessful => {
            if (copySuccessful) {
                this.copyEmbedCodeSuccess(record);
                this.emitActionPerformed(action, record, true);
            } else {
                this.copyEmbedCodeFailure(record);
                this.emitActionPerformed(action, record, false);
            }
        });
    }

    private copyEmbedCodeSuccess(record: ApiFragment): void {
        this.createAndAddNewToast('action-toasts.copy-embed-code.success', NotificationType.Success);
    }

    private copyEmbedCodeFailure(record: ApiFragment): void {
        this.createAndAddNewToast('action-toasts.copy-embed-code.error', NotificationType.Error);
    }

    //#endregion

    //#region Show Error messages

    private onShowErrors(record: ApiFragment): void {
        const title: string = this.translate('items.record-errors-dialog.title');

        const adminEmail: string | undefined = this.configService.getInstant('adminEmail');
        const detailUrl$: Observable<string[]> = this.urlBuilderService.buildDetailUrlArray([record.getDetailUrl()]);

        const dialogConfig: DialogConfig = {
            title,
            options: {
                style: DialogStyle.ExtraWide
            },
            data: {
                recordsData: [record.metadata],
                adminEmail,
                detailUrl$
            }
        };
        this.dialogService.open(RecordErrorDialogComponent, dialogConfig);
    }

    //#endregion

    //#region Show Processing Progress

    private onShowProcessingProgress(record: ApiFragment): void {
        const title: string = this.translate('processing-progress-dialog.title');

        const dialogConfig: DialogConfig = {
            title,
            options: {
                style: DialogStyle.Normal
            },
            data: {
                record
            }
        };
        this.dialogService.open(ProcessingProgressDialogContainerComponent, dialogConfig);
    }

    //#endregion

    //#region Utils
    private navigateWithData(route: string, data: any): void {
        this.router.navigate([route], { state: data });
    }
    //#endregion
}
